import {SelectionModel} from '@angular/cdk/collections';
import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CpeTo } from 'src/app/shared/models/cpeTo';
import {NavigationService} from '../../navigation/navigation.service';
import {SharedService} from '../../shared/shared.service';
import {BehaviorSubject, merge, of, Subject, Subscription} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, startWith, switchMap, tap} from 'rxjs/operators';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { UpdateCpeComponent } from '../update-cpe/update-cpe.component';
import { CpeService } from '../cpe.service';
import { CreateCpeComponent } from '../create-cpe/create-cpe.component';

@Component({
    selector: 'app-list-cpe-systems',
    templateUrl: './list-cpe.component.html',
    styleUrls: ['./list-cpe.component.scss'],
    encapsulation : ViewEncapsulation.None,
})

export class ListCpeComponent implements OnInit, OnDestroy {
    readonly CPENAME = 'cpeName';
    readonly OPTIONS = 'options';

    @Input() fromSettings: boolean;
    isLoading: boolean = true;
    isDeletingCpe: boolean = false;
    displayFailureMsg: boolean = false;
    dialogRef: MatDialogRef<unknown, any>;
    orgDialogRef: MatDialogRef<unknown, any>;
    All: string = 'All';
    cpeList: CpeTo[] = [];
    displayedColumns: string[] = [this.CPENAME, this.OPTIONS];
    dataSource = new MatTableDataSource<CpeTo>(this.cpeList);
    selection = new SelectionModel<CpeTo>(true, []);
    selectedCpe: Array<any> = [];
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    paginatorLength: number = 20;
    totalData: number = 0;
    private searchTerm$ = new BehaviorSubject<string>('');
    private refresh$ = new Subject<void>();
    private listSubscription: Subscription;

    constructor(public dialog: MatDialog,
                public navigation: NavigationService,
                public cpeService: CpeService,
                public sharedService: SharedService,
                private cdref: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.sort.active = this.CPENAME;
        this.sort.direction = 'asc';
        this.sort.disableClear = true;
        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
        this.paginator.pageSize = 10;
        this.setupCpeList();
    }

    ngOnDestroy() {
        this.listSubscription?.unsubscribe();
    }

    ngAfterContentChecked() {
         this.cdref.detectChanges();
    }

    createCpe() {
       this.dialogRef = this.dialog.open(CreateCpeComponent, {
                   data: this.selectedCpe,
                   width: '550px'
               });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.getCpeList();
            }
        });
    }

    applyFilter(filterValue: string) {
        this.searchTerm$.next(filterValue.trim());
    }

    editCpe(object) {
        this.dialogRef = this.dialog.open(UpdateCpeComponent, {
            data: object,
            width: '550px'
        });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.getCpeList();
            }
        });
    }

    getCpeList() {
        this.refresh$.next();
    }

    setupCpeList() {
        const search$ = this.searchTerm$.pipe(debounceTime(500), distinctUntilChanged(), tap(() => this.paginator.pageIndex = 0));
        this.listSubscription = merge(search$, this.sort.sortChange, this.paginator.page, this.refresh$)
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoading = true;
                    return this.cpeService.getCpePage(this.searchTerm$.value, this.sort.active,
                        this.sort.direction || 'asc', this.paginator.pageIndex, this.paginator.pageSize)
                        .pipe(catchError(() => of(null)));
                })
            )
            .subscribe(data => {
                this.isLoading = false;
                this.displayFailureMsg = data === null;
                this.cpeList = data?.content ?? [];
                this.totalData = data?.totalElements ?? 0;
                this.paginatorLength = this.totalData > 20 ? this.totalData : 20;
                // Sorting/paging are server-side, so the data source only holds the current page
                this.dataSource = new MatTableDataSource<CpeTo>(this.cpeList);
            });
    }

    deleteCpe(object) {
            Swal.fire({
                title: 'Are you sure?',
                text: 'Delete the CPE System "' + object.cpeName + '"',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No, Cancel',
            }).then((result) => {
                if (result.value) {
                    this.isDeletingCpe = true;
                    this.cpeService.deleteCpe(object).subscribe({
                        next: data => {
                            this.isDeletingCpe = false;
                            this.sharedService.showAlert('success', 'CPE System ' + object.cpeName + ' has been deleted.');
                            this.getCpeList();
                        },
                        error: error => {
                            this.isDeletingCpe = false;
                            this.sharedService.handleErrors(error,
                                [
                                    {"condition": (error.status === 400 && Object.keys(error.error).length > 0),
                                        "msg": error.error[Object.keys(error.error)[0]]
                                            .replace(/</g, '&lt;')
                                            .replace(/>/g, '&gt;')}
                                ], "Unable to delete CPE System");
                        },
                        complete: () => {}
                    });
                }
            });
    }

    syncPrimaryPaginator(event: PageEvent) {
        this.paginator.pageIndex = event.pageIndex;
        this.paginator.pageSize = event.pageSize;
        this.paginator.page.emit(event);
    }
}
